import { inject, injectable } from 'inversify'
import { makeObservable, observable, runInAction } from 'mobx'
import type { UserRole } from '@shared/index'
import { HttpGateway } from '@core/services/http.gateway'
import { Types } from '@core/ioc/types'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import { MessagePacking, type ActionFailurePm, type ActionResponsePm } from '@core/feedback/feedback.packing'
import { mapReadFailure } from '@core/services/gateway-failure.helpers'
import { z } from '@core/validation/validation.schema'
import { OperationRepository } from '@core/operations/operation.repository'
import type {
  OperationHandle,
  OperationOwner,
  OperationResult,
  ReadonlyOperationState,
} from '@core/operations/operation.types'
import {
  defineOperation,
  operationFailed,
  operationGroup,
  operationId,
  operationKey,
  operationSucceeded,
} from '@core/operations/operation.helpers'
import { FreshnessRepository } from '@core/infra/freshness/freshness.repository'

/** Same shape as LoadBooksFailure: ActionFailurePm plus the retryability signal the load's policy needs. */
export type LoadAdminUsersFailure = ActionFailurePm & Readonly<{ retryable: boolean }>

export type AdminUserPm = {
  readonly email: string
  readonly role: UserRole
  readonly deletedAt: string | null
  readonly assetCount: number
  readonly bookCount: number
}

const AdminUserListDtoSchema = z.array(
  z.object({
    email: z.string(),
    role: z.enum(['user', 'admin']),
    deletedAt: z.string().nullable(),
    assetCount: z.number(),
    bookCount: z.number(),
  }),
)

const ADMIN_USERS_GROUP = operationGroup('admin-users')

// Same policy shape as books.load/assets.load — this is the same kind of
// small list GET, so it earns the same 15s ceiling (P-47: the value follows
// the operation's shape, and here the shapes genuinely match).
export const LOAD_ADMIN_USERS_OPERATION = defineOperation<null, LoadAdminUsersFailure>({
  id: operationId('admin.users.load'),
  key: () => operationKey('admin-users'),
  groups: () => [operationGroup('session'), ADMIN_USERS_GROUP],
  pending: { kind: 'join' },
  safety: { kind: 'read' },
  timeout: {
    kind: 'after',
    milliseconds: 15_000,
    toFailure: () => ({ code: 'temporarily-unavailable', retryable: false }),
  },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

// One definition per admin mutation: all POST/DELETE against a single user,
// keyed by the target email so two admins editing different users don't
// collide, `ignore` because a second click on the same row while one write is
// pending is a double-click, not new intent (same reasoning as the auth
// forms). No retry — non-idempotent writes never retry (P-42's own guard).
//
// OperationKey only allows [a-z0-9:._-], so the email is folded into that
// alphabet. Collisions between two emails mapping to the same key are
// harmless: the key only scopes double-click suppression.
const emailKey = (email: string) => operationKey(email.toLowerCase().replace(/[^a-z0-9:._-]+/g, '-'))

const adminWriteOperation = (name: string) =>
  defineOperation<string, ActionFailurePm>({
    id: operationId(`admin.users.${name}`),
    key: (email) => emailKey(email),
    groups: () => [operationGroup('session'), ADMIN_USERS_GROUP],
    pending: { kind: 'ignore' },
    safety: { kind: 'non-idempotent-write' },
    timeout: {
      kind: 'after',
      milliseconds: 10_000,
      toFailure: () => ({ code: 'temporarily-unavailable' }),
    },
    retry: { kind: 'none' },
    debounce: { kind: 'none' },
  })

// Its own shape, not adminWriteOperation('create'): the argument is the new
// account's email + password, not an existing user's email alone.
export const CREATE_USER_OPERATION = defineOperation<{ email: string; password: string }, ActionFailurePm>({
  id: operationId('admin.users.create'),
  key: (args) => emailKey(args.email),
  groups: () => [operationGroup('session'), ADMIN_USERS_GROUP],
  pending: { kind: 'ignore' },
  safety: { kind: 'non-idempotent-write' },
  timeout: {
    kind: 'after',
    milliseconds: 10_000,
    toFailure: () => ({ code: 'temporarily-unavailable' }),
  },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

export const SET_USER_ROLE_OPERATION = adminWriteOperation('set-role')
export const RESET_USER_PASSWORD_OPERATION = adminWriteOperation('reset-password')
export const SOFT_DELETE_USER_OPERATION = adminWriteOperation('soft-delete')
export const RESTORE_USER_OPERATION = adminWriteOperation('restore')

/**
 * Backs the admin user-management page (core/infra/authentication/admin/) —
 * a cross-cutting ops capability like AdminErrorLookupRepository, not a
 * domain feature, so it lives in core/infra/ (P-35) rather than
 * features/ — admin is a mode of the authentication capability, not a place
 * of its own (spec/30-structure.md). Talks to the /admin/users endpoints
 * (backend/src/routes/admin.routes.ts), which 403 for a non-admin, 404 an
 * unknown email, and 409 self-demotion/self-deletion — all real ActionFailurePm
 * codes the Presenter can surface.
 *
 * Every successful mutation re-runs the list load: the list is small, and a
 * refetch keeps this Repository free of client-side row-merging logic that
 * could drift from what the backend would actually return.
 */
@injectable()
export class AdminUsersRepository implements UserScopedState {
  adminUserListPm: readonly AdminUserPm[] | null = null

  private readonly dataGateway: HttpGateway
  private readonly freshnessRepository: FreshnessRepository
  private readonly operationOwner: OperationOwner
  private readonly loadOperation: OperationHandle<null, LoadAdminUsersFailure>
  private readonly createOperation: OperationHandle<{ email: string; password: string }, ActionFailurePm>
  private readonly setRoleOperation: OperationHandle<string, ActionFailurePm>
  private readonly resetPasswordOperation: OperationHandle<string, ActionFailurePm>
  private readonly softDeleteOperation: OperationHandle<string, ActionFailurePm>
  private readonly restoreOperation: OperationHandle<string, ActionFailurePm>

  constructor(
    @inject(Types.IDataGateway) dataGateway: HttpGateway,
    @inject(OperationRepository) operationRepository: OperationRepository,
    @inject(FreshnessRepository) freshnessRepository: FreshnessRepository,
  ) {
    this.dataGateway = dataGateway
    this.freshnessRepository = freshnessRepository
    this.operationOwner = operationRepository.createOwner({ id: 'admin-users.repository', lifetime: 'session' })
    this.loadOperation = this.operationOwner.register(LOAD_ADMIN_USERS_OPERATION)
    this.createOperation = this.operationOwner.register(CREATE_USER_OPERATION)
    this.setRoleOperation = this.operationOwner.register(SET_USER_ROLE_OPERATION)
    this.resetPasswordOperation = this.operationOwner.register(RESET_USER_PASSWORD_OPERATION)
    this.softDeleteOperation = this.operationOwner.register(SOFT_DELETE_USER_OPERATION)
    this.restoreOperation = this.operationOwner.register(RESTORE_USER_OPERATION)

    makeObservable(this, {
      adminUserListPm: observable.ref,
    })
  }

  get isLoaded(): boolean {
    return this.adminUserListPm !== null
  }

  // Route-entry preload target (RoutesConfig) — same policy as BooksRepository.
  loadIfNeeded = async (): Promise<void> => {
    if (this.isLoaded && this.freshnessRepository.isFresh('admin.users')) return
    await this.load()
  }

  get loadState(): ReadonlyOperationState<LoadAdminUsersFailure> {
    return this.loadOperation.stateFor(null)
  }

  load = async (): Promise<ActionResponsePm> => {
    const result = await this.loadOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.get('/admin/users', { signal, idempotencyKey: null })
        if (!response.success) {
          return operationFailed(mapReadFailure(response))
        }

        const parsed = AdminUserListDtoSchema.safeParse(response.result)
        if (!parsed.success) {
          return operationFailed({ code: 'invalid-external-data', retryable: false })
        }

        const users = Object.freeze(
          parsed.data.map((dto): AdminUserPm =>
            Object.freeze({
              email: dto.email,
              role: dto.role,
              deletedAt: dto.deletedAt,
              assetCount: dto.assetCount,
              bookCount: dto.bookCount,
            }),
          ),
        )
        return operationSucceeded(users)
      },
      {
        succeeded: (users) => {
          this.adminUserListPm = users
          this.freshnessRepository.markLoaded('admin.users')
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  createUser = async (email: string, password: string): Promise<ActionResponsePm> => {
    const result = await this.createOperation.run(
      { email, password },
      async ({ signal }) => {
        const response = await this.dataGateway.post(
          '/admin/users',
          { email, password },
          { signal, idempotencyKey: null },
        )
        if (!response.success) {
          const pm = MessagePacking.unpackGatewayResponseToPm(response)
          return pm.success ? operationFailed({ code: 'temporarily-unavailable' }) : operationFailed(pm.failure)
        }
        return operationSucceeded(null)
      },
      { succeeded: null, failed: null },
    )

    // Same refetch-on-success shape as runWrite: the new account has to show
    // up in the list, and a refetch keeps this Repository free of
    // client-side row-merging logic that could drift from the backend.
    if (result.kind === 'succeeded') await this.load()
    return this.toActionResponsePm(result)
  }

  setRole = (email: string, role: UserRole): Promise<ActionResponsePm> =>
    this.runWrite(this.setRoleOperation, email, ({ signal }) =>
      this.dataGateway.post(
        `/admin/users/${encodeURIComponent(email)}/role`,
        { role },
        { signal, idempotencyKey: null },
      ),
    )

  resetPassword = (email: string, password: string): Promise<ActionResponsePm> =>
    this.runWrite(this.resetPasswordOperation, email, ({ signal }) =>
      this.dataGateway.post(
        `/admin/users/${encodeURIComponent(email)}/password`,
        { password },
        { signal, idempotencyKey: null },
      ),
    )

  softDelete = (email: string): Promise<ActionResponsePm> =>
    this.runWrite(this.softDeleteOperation, email, ({ signal }) =>
      this.dataGateway.delete(`/admin/users/${encodeURIComponent(email)}`, { signal, idempotencyKey: null }),
    )

  restore = (email: string): Promise<ActionResponsePm> =>
    this.runWrite(this.restoreOperation, email, ({ signal }) =>
      this.dataGateway.post(`/admin/users/${encodeURIComponent(email)}/restore`, {}, { signal, idempotencyKey: null }),
    )

  /**
   * Shared mutation shape: run the write, and on success refetch the list so
   * the PM reflects what the backend now holds. The refetch failing is
   * reported by `loadState` (the Presenter folds it into the VM), not by the
   * write's own response — the write itself did succeed.
   */
  private runWrite = async (
    operation: OperationHandle<string, ActionFailurePm>,
    email: string,
    request: (context: { signal: AbortSignal | null }) => ReturnType<HttpGateway['post']>,
  ): Promise<ActionResponsePm> => {
    const result = await operation.run(
      email,
      async ({ signal }) => {
        const response = await request({ signal })
        if (!response.success) {
          const pm = MessagePacking.unpackGatewayResponseToPm(response)
          return pm.success ? operationFailed({ code: 'temporarily-unavailable' }) : operationFailed(pm.failure)
        }
        return operationSucceeded(null)
      },
      { succeeded: null, failed: null },
    )

    if (result.kind === 'succeeded') await this.load()
    return this.toActionResponsePm(result)
  }

  resetUserScopedState = (): void => {
    this.operationOwner.resetAll({ kind: 'session-reset' })
    runInAction(() => {
      this.adminUserListPm = null
    })
  }

  private toActionResponsePm(result: OperationResult<unknown, ActionFailurePm>): ActionResponsePm {
    switch (result.kind) {
      case 'succeeded':
        return MessagePacking.succeeded()
      case 'failed':
        return { success: false, failure: result.error }
      case 'cancelled':
      case 'skipped':
        return MessagePacking.cancelled()
    }
  }
}
